import React from 'react';
import { roomParts, type Leaf, type ZoneLayout } from '../../utils/floorRoom';

/**
 * Ο χώρος της ζώνης όπως τον έστησε η Sala, κάτω από τα τραπέζια του ταμείου:
 * δάπεδο με τοίχους (ή ανοιχτή αυλή), πόρτες με την τροχιά τους, παράθυρα, μπαρ,
 * κολώνες, φυτά. Μόνο εικόνα (pointer-events: none) — τα τραπέζια μένουν από πάνω.
 * Χρώματα από το θέμα (--text-3 τοίχοι, --surface-1 δάπεδο), άρα σωστό και στο σκούρο.
 */
const WALL_COLOR = 'hsl(var(--text-3))';
const ARC = '1.5px dashed hsl(var(--line-strong))';

function leafStyle(l: Leaf): React.CSSProperties {
  const solid = `2px solid ${WALL_COLOR}`;
  const st: React.CSSProperties = { left: l.left, top: l.top, width: l.width, height: l.height };
  const r = `${l.width}px`;
  if (l.hinge === 'l' || l.hinge === 'r') {
    st.borderLeft = l.hinge === 'l' ? solid : ARC;
    st.borderRight = l.hinge === 'r' ? solid : ARC;
    if (l.open === 'down') { st.borderBottom = ARC; st.borderRadius = l.hinge === 'l' ? `0 0 ${r} 0` : `0 0 0 ${r}`; }
    else { st.borderTop = ARC; st.borderRadius = l.hinge === 'l' ? `0 ${r} 0 0` : `${r} 0 0 0`; }
  } else {
    st.borderTop = l.hinge === 't' ? solid : ARC;
    st.borderBottom = l.hinge === 'b' ? solid : ARC;
    if (l.open === 'right') { st.borderRight = ARC; st.borderRadius = l.hinge === 't' ? `0 0 ${r} 0` : `0 ${r} 0 0`; }
    else { st.borderLeft = ARC; st.borderRadius = l.hinge === 't' ? `0 0 0 ${r}` : `${r} 0 0 0`; }
  }
  return st;
}

const FIXTURE: Record<string, string> = {
  bar: 'bg-surface-3 border-2 border-line-strong text-t3 rounded-[10px]',
  column: 'rounded-[4px]',
  plant: 'rounded-full bg-emerald-100 border-2 border-emerald-400 dark:bg-emerald-800/50 dark:border-emerald-500',
  label: 'text-t3',
};

const ZoneRoom: React.FC<{ layout?: ZoneLayout | null; canvas: { width: number; height: number } }> = ({ layout, canvas }) => {
  const p = roomParts(layout, canvas);
  if (!p) return null;
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <div
        className={`absolute inset-0 box-border ${p.indoor ? 'rounded-md bg-surface-1' : 'rounded-[20px] bg-surface-2'}`}
        style={p.indoor
          ? { border: `${p.wall}px solid ${WALL_COLOR}` }
          : {
            // Αυλή: χωρίς τοίχους, διακεκομμένο όριο και πλάκες δαπέδου.
            border: '3px dashed hsl(var(--text-4))',
            backgroundImage: 'repeating-linear-gradient(0deg, hsl(var(--line-strong) / 0.55) 0 1px, transparent 1px 48px), repeating-linear-gradient(90deg, hsl(var(--line-strong) / 0.55) 0 1px, transparent 1px 48px)',
          }}
      />
      {p.openings.map(o => (
        <React.Fragment key={o.key}>
          {o.gap && <div className={`absolute ${p.indoor ? 'bg-surface-1' : 'bg-surface-2'}`} style={o.gap} />}
          {o.window && (
            <div className="absolute box-border bg-surface-1" style={{
              left: o.window.left, top: o.window.top, width: o.window.width, height: o.window.height,
              ...(o.window.horiz
                ? { borderTop: `1.5px solid ${WALL_COLOR}`, borderBottom: `1.5px solid ${WALL_COLOR}` }
                : { borderLeft: `1.5px solid ${WALL_COLOR}`, borderRight: `1.5px solid ${WALL_COLOR}` }),
            }} />
          )}
          {o.leaves.map((l, i) => <div key={i} className="absolute box-border" style={leafStyle(l)} />)}
          {o.label && (
            <div className="absolute whitespace-nowrap text-[13px] font-medium text-t3"
              style={{
                left: o.label.left, top: o.label.top,
                transform: o.label.vertical ? 'translateY(-50%)' : 'translateX(-50%)',
                writingMode: o.label.vertical ? 'vertical-rl' : undefined,
              }}>
              {o.label.text}
            </div>
          )}
        </React.Fragment>
      ))}
      {p.fixtures.map(f => (
        <div key={f.key} className={`absolute box-border flex items-center justify-center ${FIXTURE[f.type]}`}
          style={{ left: f.left, top: f.top, width: f.width, height: f.height, ...(f.type === 'column' ? { background: WALL_COLOR } : null) }}>
          {(f.type === 'bar' || f.type === 'label') && f.label && (
            <span className={`whitespace-nowrap font-bold ${f.type === 'bar' ? 'text-[12px] tracking-[0.3em]' : 'text-[14px]'}`}
              style={f.vertical ? { writingMode: 'vertical-rl', transform: 'rotate(180deg)' } : undefined}>
              {f.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default ZoneRoom;
